import { useState, type ReactNode } from 'react';
import { Navigate } from 'react-router';
import type { KpiProfile } from '../../lib/kpi';
import { DashboardHeader } from './DashboardHeader';
import { DateRange } from './DateRange';
import { OperatorPicker } from './OperatorPicker';
import { KpiCards } from './KpiCards';
import { DailyChart } from './DailyChart';
import { OperatorTable } from './OperatorTable';
import { DEFAULT_WINDOW_DAYS, lastDays, windowProblem } from './dashboardWindow';
import { useProfile, useSummary, type Loadable } from './useDashboardData';

const UNAUTHORIZED = 401;
const FORBIDDEN = 403;

function Notice({ children }: { children: ReactNode }) {
  return <div className="p-6 text-center text-sm text-[var(--co-muted)]">{children}</div>;
}

/** A 401 means the session died or never passed the second factor: back to
 *  the sign-in, which starts clean. */
function Failure({ state }: { state: Extract<Loadable<unknown>, { status: 'error' }> }) {
  if (state.httpStatus === UNAUTHORIZED) return <Navigate to="/login" replace />;
  if (state.httpStatus === FORBIDDEN) return <Notice>This account has no access to the KPI dashboard.</Notice>;

  return <Notice>{state.message}</Notice>;
}

export function DashboardPage() {
  const profile = useProfile();

  if (profile.status === 'loading') return <Notice>Loading…</Notice>;
  if (profile.status === 'error') return <Failure state={profile} />;

  return <Dashboard profile={profile.data} />;
}

function Dashboard({ profile }: { profile: KpiProfile }) {
  const [range, setRange] = useState(() => lastDays(DEFAULT_WINDOW_DAYS, profile.published_through));
  const [operatorIds, setOperatorIds] = useState<number[]>([]);
  const problem = windowProblem(range);
  const summary = useSummary(problem ? null : { ...range, operatorIds });

  return (
    <div className="min-h-screen">
      <DashboardHeader profile={profile} />
      <main className="mx-auto max-w-6xl space-y-4 p-4">
        <DateRange value={range} onChange={setRange} publishedThrough={profile.published_through} problem={problem} />
        {profile.operators.length > 1 && (
          <OperatorPicker operators={profile.operators} selected={operatorIds} onChange={setOperatorIds} />
        )}
        {summary.status === 'loading' && <Notice>Loading figures…</Notice>}
        {summary.status === 'error' && <Failure state={summary} />}
        {summary.status === 'ready' && (
          <>
            <KpiCards kpis={summary.data.kpis} totals={summary.data.totals} currency={summary.data.currency} />
            <DailyChart summary={summary.data} />
            <OperatorTable summary={summary.data} />
          </>
        )}
      </main>
    </div>
  );
}
